import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { teamApi } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [accepted, setAccepted] = useState(false);

  const { data: inviteInfo, isLoading, error } = useQuery({
    queryKey: ['invite-info', token],
    queryFn: async () => {
      const response = await teamApi.getInviteInfo(token!);
      return response.data;
    },
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: () => teamApi.acceptInvite(token!),
    onSuccess: (response) => {
      setAccepted(true);
      toast.success(`You joined ${response.data.team.name}!`);
      setTimeout(() => {
        navigate(`/teams/${response.data.team.id}`);
      }, 1500);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to accept invite');
    },
  });

  // If already accepted, redirect to the team
  useEffect(() => {
    if (inviteInfo?.accepted && isAuthenticated) {
      toast('This invite has already been accepted.');
      navigate('/teams');
    }
  }, [inviteInfo, isAuthenticated, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Invalid or expired invite
  if (error || !inviteInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <span className="text-3xl font-bold text-navy-900">Clarid</span>
          </Link>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 text-sm">
              This invitation link is invalid or has expired. Please ask your team admin to send a new invite.
            </p>
            <Link to="/" className="mt-6 inline-block text-ocean-600 hover:text-ocean-700 font-medium">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (inviteInfo.expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <span className="text-3xl font-bold text-navy-900">Clarid</span>
          </Link>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="text-5xl mb-4">⏰</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invitation Expired</h2>
            <p className="text-gray-600 text-sm">
              This invitation has expired. Please ask your team admin to send a new invite.
            </p>
            <Link to="/" className="mt-6 inline-block text-ocean-600 hover:text-ocean-700 font-medium">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state after accepting
  if (accepted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome to {inviteInfo.teamName}!</h2>
            <p className="text-gray-600 text-sm">You have successfully joined the team. Taking you there now...</p>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Unauthenticated: show invite card with login/register options
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <span className="text-3xl font-bold text-navy-900">Clarid</span>
          </Link>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ocean-100 mb-4">
                <svg className="w-8 h-8 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">You've been invited!</h2>
              <p className="text-gray-600 text-sm mt-1">
                <span className="font-medium text-gray-800">{inviteInfo.inviterName}</span> has invited you to join the{' '}
                <span className="font-medium text-gray-800">{inviteInfo.teamName}</span> team on Clarid.
              </p>
            </div>

            <div className="bg-ocean-50 border border-ocean-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-ocean-800 text-center">
                To accept this invitation, you'll need a Clarid account.
              </p>
            </div>

            <div className="space-y-3">
              <Link
                to={`/register?invite=${token}`}
                className="flex w-full justify-center rounded-md border border-transparent bg-ocean-600 py-3 px-4 text-sm font-medium text-white shadow-sm hover:bg-ocean-700 transition-all"
              >
                Create an account
              </Link>
              <Link
                to={`/login?invite=${token}`}
                className="flex w-full justify-center rounded-md border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-all"
              >
                Sign in with existing account
              </Link>
            </div>

            <p className="mt-6 text-xs text-center text-gray-500">
              This invitation will expire in 7 days from when it was sent.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated: show accept button
  // Check email mismatch
  const emailMismatch = user?.email && inviteInfo.email !== user.email;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <span className="text-3xl font-bold text-navy-900">Clarid</span>
        </Link>
        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ocean-100 mb-4">
              <svg className="w-8 h-8 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Team Invitation</h2>
            <p className="text-gray-600 text-sm mt-1">
              <span className="font-medium text-gray-800">{inviteInfo.inviterName}</span> has invited you to join the{' '}
              <span className="font-medium text-gray-800">{inviteInfo.teamName}</span> team.
            </p>
          </div>

          {emailMismatch ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 text-center">
                This invitation was sent to <span className="font-medium">{inviteInfo.email}</span>, but you are logged in as{' '}
                <span className="font-medium">{user?.email}</span>. Please log in with the correct account.
              </p>
            </div>
          ) : (
            <div className="bg-ocean-50 border border-ocean-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-ocean-800 text-center">
                Joining this team will give you access to collaborate on compliance checking and share reports.
              </p>
            </div>
          )}

          {!emailMismatch && (
            <button
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending}
              className="flex w-full justify-center rounded-md border border-transparent bg-ocean-600 py-3 px-4 text-sm font-medium text-white shadow-sm hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {acceptMutation.isPending ? 'Joining...' : `Join ${inviteInfo.teamName}`}
            </button>
          )}

          <Link to="/teams" className="mt-4 flex w-full justify-center text-sm text-gray-500 hover:text-gray-700">
            No thanks, go to my teams
          </Link>
        </div>
      </div>
    </div>
  );
}
